import { useTitle } from "@/Utilities/Hooks";
import HomeLayout from "@/Layouts/HomeLayout";
import { RecommendationGuide } from "@/Components/RecommendationGuide/RecommendationGuide";
import { Button, FormError } from "@/Components";
import Slider from "react-slick";
import "@/../css/slick.css";
import "@/../css/slick-theme.css";
import React, { useEffect, useRef, useState } from "react";
import { X } from "react-feather";
import { usePage } from "@inertiajs/inertia-react";
import { toast } from "react-toastify";
import { Inertia } from "@inertiajs/inertia";
import { LabeledInput, LabeledTextarea } from "@/Components/Form";

const Home = ({}) => {
  useTitle("The tools you need to reach your climate goals");

  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const features = [
    {
      icon: "/images/home/sliders.svg",
      title: "Tailored solutions",
      description:
        "Receive personalised recommendations based on your unique business needs and sustainability goals.",
    },
    {
      icon: "/images/home/checklists.svg",
      title: "Stay compliant",
      description:
        "Ensure adherence to regulations like GHG-Protocol, ESG, CSRD, and GRI, with expert guidance every step of the way.",
    },
    {
      icon: "/images/home/love-earth.svg",
      title: "Increase competitiveness",
      description:
        "Reducing your carbon footprint and becoming more sustainable makes your products or services more attractive on the market.",
    },
  ];

  return (
    <>
      <section className="mt-20 w-full max-w-[1182px] self-center max-md:mt-0">
        <div className="flex gap-5 max-md:flex-col max-md:gap-0">
          <div className="flex w-7/12 flex-col max-md:ml-0 max-md:w-full">
            <div className="mb-auto flex flex-col self-stretch max-xl:px-4 max-md:mt-10 lg:mt-20">
              <h1 className="text-balance text-6xl leading-[70px] max-md:text-4xl max-md:leading-10">
                The tools you need to reach your climate goals
              </h1>
              <p className="mt-6 text-md leading-1 md:text-2xl md:leading-9">
                With Emma, you get both a complete reporting tool and support
                from experienced advisors.
              </p>
            </div>
          </div>
          <div className="ml-auto flex w-5/12 flex-col max-md:ml-0 max-md:w-full">
            <img
              src="/images/home/why-emma.png"
              className="w-full max-md:mx-auto max-md:mt-10 max-md:max-w-xs"
              alt=""
              role="presentation"
            />
          </div>
        </div>
      </section>

      <div
        className="relative -mb-10 flex justify-center bg-yellow lg:-mb-20"
        id="test"
      >
        <div className="absolute left-0 top-0 z-0 h-[50%] w-full bg-gray-lighter" />
        <section className="z-10 w-full max-w-[1182px] self-center">
          <RecommendationGuide />
        </section>
      </div>

      <section className="flex justify-center bg-yellow">
        <div className="w-full max-w-[1182px] self-center py-20 md:px-4 md:pt-32 lg:pt-20 xl:px-0">
          <div className="flex gap-8 max-md:flex-col max-md:gap-0 max-md:px-4">
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="flex w-full max-w-[1182px] flex-wrap self-center">
        <div className="relative w-full max-lg:aspect-[16/9] lg:w-2/5">
          <img
            src="/images/home/tall-trees.jpg"
            alt="Two green trees in an urban environment"
            loading="lazy"
            className="absolute h-full w-full object-cover"
          />
        </div>
        <div className="flex w-full grow flex-col bg-white p-16 text-md leading-7 max-md:px-4 lg:w-3/5">
          <h2 className="font-rozhaone text-5xl leading-[52.8px] max-md:text-4xl">
            How it works
          </h2>
          <p className="mt-4">
            There are many sustainability challenges that need to be balanced
            within an organisation. Resource efficiency, supply chain
            management, legal compliance, consumer demand, data collection and
            analysis - just to mention a few. Overcoming these challenges
            requires collaboration between many departments. Therefore, we have
            created Emma to facilitate effective teamwork.
          </p>
          <h3 className="mt-5 font-semibold">Tailored set up</h3>
          <p className="mt-1">
            Our sustainability specialists help you prioritise an effective data
            collection with a tailored setup according to your sustainability
            reporting needs and long-term strategic goals. We involve relevant
            departments and help you open the doors to effective collaboration
            across the value chain.
          </p>
          <h3 className="mt-5 font-semibold">Insights and knowledge</h3>
          <p className="mt-1">
            Get powerful insight with our database, integrations and tailored
            methodologies. Access your reports and focus on building business
            through sustainability data, instead of putting all resources on
            reporting.
          </p>
          <h3 className="mt-5 font-semibold">Future-proof reporting</h3>
          <p className="mt-1">
            Our goal is to make you confident enough to report and adapt Emma on
            your own. This way, you can feel safe and confident that you are on
            track with sustainability reporting.
          </p>
          <Button className="mt-8" onClick={() => setIsContactDialogOpen(true)}>
            Contact us
          </Button>

          <ContactDialog
            openModal={isContactDialogOpen}
            closeModal={() => setIsContactDialogOpen(false)}
          />
        </div>
      </section>

      <AchievementSlider />

      <section className="flex w-full max-w-[1182px] flex-row-reverse flex-wrap self-center">
        <div className="relative w-full max-lg:aspect-[16/9] lg:w-2/5">
          <img
            src="/images/home/tall-trees.jpg"
            alt="Two green trees in an urban environment"
            loading="lazy"
            className="absolute h-full w-full object-cover"
          />
        </div>
        <div className="flex w-full grow flex-col bg-white p-16 text-md leading-7 max-md:px-4 lg:w-3/5">
          <h2 className="font-rozhaone text-5xl leading-[52.8px] max-md:text-4xl">
            What we do and why
          </h2>
          <p className="mt-4">
            We aim to become the first generation in world history to bend the
            climate change curve downwards. That is why we offer a simple
            solution for companies and organisations to become more sustainable
            – fast, simple and without large investments.
          </p>
          <p className="mt-1">
            Our experienced sustainability specialists will help you prioritise
            and implement effective actions to reduce emissions to air and grow
            your business. We help you go from talking to action, secure
            reporting requirements, and gain a competitive edge through
            sustainability.
          </p>
          <h3 className="mt-5 font-semibold">We can help you with</h3>
          <ul className="ml-6 mt-2 list-disc">
            <li className="mb-2">
              Double materiality analysis according to the ESRS framework
            </li>
            <li className="mb-2">
              Greenhouse gas accounting according to the GHG-protocol
            </li>
            <li className="mb-2">
              Sustainability reporting and data collection according to CSRD
            </li>
            <li className="mb-2">
              Life cycle analysis and environmental product declarations
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};

Home.layout = (page) => <HomeLayout>{page}</HomeLayout>;

export default Home;

function FeatureItem({ icon, title, description }) {
  return (
    <div className="flex flex-col max-md:ml-0 max-md:w-full">
      <div className="flex flex-col  max-md:mt-10">
        <img src={icon} alt="" className="aspect-square w-12" />
        <h3 className="mt-4 font-overpass font-rozhaone text-3xl leading-9">
          {title}
        </h3>
        <p className="mt-2 text-md leading-7">{description}</p>
      </div>
    </div>
  );
}

function AchievementSlider() {
  const cases = [
    {
      title: "Smart Energy Cup",
      description:
        "We helped Smart Energy Cup reduce their emissions by 31% by changing the racing schedule for cross-country skiing events and minimise unnecessary transportation.",
      percentage: "31%",
      backgroundColor: "#1F4444",
      color: "#fff",
    },
    {
      title: "Valbo Trä",
      description:
        "By switching from diesel to Hydrotreated vegetable oil we helped Valbo Trä, a Swedish supplier of processed wood, reduce their emissions from vehicles by 90%.",
      percentage: "90%",
      backgroundColor: "#DDEBFC",
      color: "#363636",
    },
    {
      title: "Svenska Takelement",
      description:
        "We helped Svenska takelement, a supplier of prefabricated roof panels, reduce emissions by 10% by minimizing production waste.",
      percentage: "10%",
      backgroundColor: "#F3FFAE",
      color: "#363636",
    },
    {
      title: "Persson Invest",
      description:
        "We helped the conglomerate AB Persson Invest, a family-owned investment company in the Nordics, reduce their emissions with 39% by switching to renewable energy.",
      percentage: "39%",
      backgroundColor: "#DDEBFC",
      color: "#363636",
    },
  ];

  const sortedCases = useRef(cases.sort(() => Math.random() - 0.5));

  const [colors, setColors] = useState({
    backgroundColor: sortedCases.current[0].backgroundColor,
    color: sortedCases.current[0].color,
  });

  const settings = {
    dots: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    adaptiveHeight: true,
    autoplaySpeed: 7500,
    pauseOnHover: true,
    beforeChange: (oldIndex, newIndex) => {
      setColors({
        backgroundColor: sortedCases.current[newIndex].backgroundColor,
        color: sortedCases.current[newIndex].color,
      });
    },
  };

  return (
    <>
      <section
        className="achievement-slider-wrapper flex justify-center"
        style={{
          "--achievement-slider-background-color": colors.backgroundColor,
          "--achievement-slider-color": colors.color,
        }}
      >
        <div className="achievement-slider w-full max-w-[1182px] self-center pb-20 pt-10 lg:pb-28 lg:pt-20">
          <Slider {...settings}>
            {sortedCases.current.map((item, index) => (
              <div
                key={index}
                className="achievement-slider-item flex flex-wrap items-start pb-8 max-md:flex-col max-md:gap-4 lg:pb-16"
              >
                <p className="w-full font-rozhaone text-[100px] leading-none md:w-2/5 md:px-4 md:text-center lg:text-[160px]">
                  {item.percentage}
                </p>
                <div className="w-full md:w-3/5 md:px-4">
                  <h2 className="mb-2 font-rozhaone text-5xl leading-[52.8px] max-md:text-4xl">
                    {item.title}
                  </h2>
                  <p className="text-xl">{item.description}</p>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>
    </>
  );
}

function ContactDialog({ openModal, closeModal }) {
  const ref = useRef();

  useEffect(() => {
    if (openModal) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [openModal]);

  const errors = usePage().props.errors;

  const [data, setData] = useState({});
  const [isLocked, setIsLocked] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLocked(true);

    const id = toast.loading("Please wait...");

    Inertia.post(route("home.contact"), data, {
      preserveScroll: true,
      onSuccess: () => {
        toast.update(id, {
          render:
            "Thank you for contacting us! We will get in touch with you soon",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        setData({});
        closeModal();
      },
      onError: () => {
        toast.update(id, {
          render: "Something went wrong. Please try again later",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
      onFinish: () => {
        setIsLocked(false);
      },
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <dialog
      ref={ref}
      onCancel={closeModal}
      className="w-full max-w-[1182px] max-xl:max-w-[calc(100%-2rem)]"
    >
      <div className="relative flex justify-center gap-5 bg-white px-10 py-10 max-md:flex-col max-md:gap-0 lg:px-40 lg:py-40">
        <button
          className="absolute right-4 top-4 lg:right-10 lg:top-10"
          onClick={closeModal}
          aria-label="Close recommendation guide"
        >
          <X size={24} aria-hidden="true" />
        </button>

        <img
          loading="lazy"
          src="/images/decorative-polygon-yellow.svg"
          alt=""
          role="presentation"
          className="absolute bottom-0 left-0 top-0 max-w-full"
        />
        <form
          className="relative z-10 mx-auto flex w-full max-w-[584px] flex-col gap-4 max-md:mt-10 max-md:px-4"
          onSubmit={handleSubmit}
        >
          <div className="flex gap-8 max-md:flex-col max-md:items-center">
            <div>
              <h2 className="mb-2 font-rozhaone text-3xl leading-9">
                Contact us
              </h2>
            </div>
          </div>

          <LabeledInput
            title="Name*"
            name="name"
            placeholder="Enter your name"
            required
            autoComplete="name"
            value={data?.name || ""}
            onChange={onChange}
            error={errors?.name}
          />
          <LabeledInput
            title="Company*"
            name="company"
            placeholder="Enter your company's name"
            required
            autoComplete="organization"
            value={data?.company || ""}
            onChange={onChange}
            error={errors?.company}
          />
          <LabeledInput
            title="Email*"
            name="email"
            placeholder="Enter your email address"
            type="email"
            required
            autoComplete="email"
            value={data?.email || ""}
            onChange={onChange}
            error={errors?.email}
          />
          <LabeledTextarea
            title="Message*"
            name="other"
            value={data?.other || ""}
            required
            onChange={onChange}
            error={errors?.other}
          />

          <div>
            <input
              type="checkbox"
              id="terms"
              name="terms"
              className="mr-2"
              required
              checked={data?.terms || false}
              onChange={(e) =>
                setData((prev) => ({ ...prev, terms: e.target.checked }))
              }
            />
            <label htmlFor="terms">
              I have read and understood the&nbsp;
              <a
                target="_blank"
                className="underline hover:no-underline"
                href="https://www.dille.se/integritet/"
              >
                privacy policy
              </a>
              *
            </label>
            {errors?.term && <FormError error={errors.terms} />}
          </div>

          <Button disabled={isLocked} className="mt-4">
            Submit
          </Button>
        </form>
      </div>
    </dialog>
  );
}
